import type React from 'react';
import { useEffect, useState } from 'react';

import { isMotionReduced } from 'src/app/shared/utils/accessibility';

const TRANSITION_DURATION = 300;

type Phase = 'hidden' | 'mounting' | 'visible' | 'hiding';

type FadeTransition = { shouldRender: false; style?: never } | { shouldRender: true; style?: React.CSSProperties };

function getNextPhase(show: boolean, phase: Phase, reduceMotion: boolean): Phase {
	if (show && (phase === 'hidden' || phase === 'hiding')) {
		return reduceMotion ? 'visible' : 'mounting';
	}
	if (!show && (phase === 'visible' || phase === 'mounting')) {
		return reduceMotion ? 'hidden' : 'hiding';
	}
	return phase;
}

/**
 * Manages a fade-in/fade-out lifecycle using a 4-phase state machine:
 *
 *   hidden ──(show)──► mounting ──(rAF)──► visible
 *   visible ──(hide)──► hiding ──(timeout)──► hidden
 *
 * - `hidden`:   Not in the DOM.
 * - `mounting`: In the DOM at opacity 0, waiting one animation frame
 *               so the browser paints before transitioning to opacity 1.
 * - `visible`:  In the DOM at opacity 1.
 * - `hiding`:   In the DOM at opacity 0, waiting for the transition
 *               duration to elapse before unmounting.
 *
 * When the user prefers reduced motion, intermediate phases are skipped
 * and the component mounts/unmounts instantly without opacity styles.
 */
export function useFadeTransition(show: boolean): FadeTransition {
	const reduceMotion = isMotionReduced();
	const [phase, setPhase] = useState<Phase>(show ? 'visible' : 'hidden');

	const nextPhase = getNextPhase(show, phase, reduceMotion);
	if (nextPhase !== phase) {
		setPhase(nextPhase);
	}

	useEffect(() => {
		if (phase === 'mounting') {
			const id = requestAnimationFrame(() => setPhase('visible'));
			return () => cancelAnimationFrame(id);
		}

		if (phase === 'hiding') {
			const id = setTimeout(() => setPhase('hidden'), TRANSITION_DURATION);
			return () => clearTimeout(id);
		}

		return undefined;
	}, [phase]);

	if (phase === 'hidden') return { shouldRender: false };

	if (reduceMotion) return { shouldRender: true };

	return {
		shouldRender: true,
		style: {
			opacity: phase === 'visible' ? 1 : 0,
			transition: `opacity ${TRANSITION_DURATION}ms ease`,
		},
	};
}
